'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdVerified, MdAdd, MdDelete } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    createAuthorization,
    updateAuthorization,
    getAuthorization,
    CreateAuthorizationData,
    UpdateAuthorizationData,
    AuthorizationUrgency,
    AUTHORIZATION_URGENCY_LABELS,
} from '@/lib/api/billing/authorization-api'
import { ClientSelector } from '@/components/client/ClientSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { getHealthPlans, HealthPlan } from '@/lib/api/medical/health-plan-api'
import { format } from 'date-fns'

interface AuthorizationItem {
    procedureCode: string
    procedureName: string
    quantity: number
    justification?: string
}

interface FormData {
    healthPlanId: string
    clientId: string
    professionalId: string
    cardNumber: string
    urgency: AuthorizationUrgency
    requestDate: string
    clinicalIndication: string
    notes: string
}

export default function AuthorizationFormPage() {
    const { user, isProfessional } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [healthPlans, setHealthPlans] = useState<HealthPlan[]>([])
    const [items, setItems] = useState<AuthorizationItem[]>([])

    const [formData, setFormData] = useState<FormData>({
        healthPlanId: '',
        clientId: '',
        professionalId: '',
        cardNumber: '',
        urgency: 'ROUTINE',
        requestDate: format(new Date(), 'yyyy-MM-dd'),
        clinicalIndication: '',
        notes: '',
    })

    useEffect(() => {
        if (user?.companyId) {
            loadData()
            if (isEditing && id) {
                loadAuthorization()
            }
        }
    }, [id, isEditing, user?.companyId])

    // Quem está logado como profissional é o solicitante: não há o que escolher,
    // e o seletor fica escondido (ver o formulário abaixo).
    useEffect(() => {
        if (isEditing || !isProfessional || !user?.professionalId) return
        setFormData(prev =>
            prev.professionalId
                ? prev
                : { ...prev, professionalId: String(user.professionalId) }
        )
    }, [isEditing, isProfessional, user?.professionalId])

    const loadData = async () => {
        if (!user?.companyId) return

        try {
            const healthPlansRes = await getHealthPlans(user.organizationId)
            if (healthPlansRes.success)
                setHealthPlans(healthPlansRes.result || [])
        } catch (error) {
            console.error('Erro ao carregar dados:', error)
        }
    }

    const loadAuthorization = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getAuthorization(parseInt(id, 10))
            if (response.success && response.result) {
                const auth = response.result
                setFormData({
                    healthPlanId: auth.healthPlanId?.toString() || '',
                    clientId: auth.clientId?.toString() || '',
                    professionalId: auth.professionalId?.toString() || '',
                    cardNumber: '',
                    urgency: auth.urgency || 'ROUTINE',
                    requestDate: auth.requestDate
                        ? format(new Date(auth.requestDate), 'yyyy-MM-dd')
                        : '',
                    clinicalIndication: auth.clinicalIndication || '',
                    notes: auth.notes || '',
                })
                if (auth.items) {
                    setItems(
                        auth.items.map(item => ({
                            procedureCode: item.procedureCode,
                            procedureName: item.procedureName,
                            quantity: item.requestedQuantity,
                            justification: item.notes || '',
                        }))
                    )
                }
            } else {
                toast.error(response.error || 'Erro ao carregar autorização')
                navigate('/billing/authorizations')
            }
        } catch (error) {
            toast.error('Erro ao carregar autorização')
            navigate('/billing/authorizations')
        } finally {
            setLoadingData(false)
        }
    }

    const updateFormField = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const addItem = () => {
        setItems(prev => [
            ...prev,
            {
                procedureCode: '',
                procedureName: '',
                quantity: 1,
            },
        ])
    }

    const removeItem = (index: number) => {
        setItems(prev => prev.filter((_, i) => i !== index))
    }

    const updateItem = (
        index: number,
        field: keyof AuthorizationItem,
        value: string | number
    ) => {
        setItems(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        )
    }

    const validateForm = (): boolean => {
        if (!formData.healthPlanId) {
            toast.error('Selecione um convenio')
            return false
        }
        if (!formData.clientId) {
            toast.error('Selecione um paciente')
            return false
        }
        if (!formData.cardNumber.trim()) {
            toast.error('Informe o número da carteirinha')
            return false
        }
        if (items.length === 0) {
            toast.error('Adicione pelo menos um procedimento')
            return false
        }
        const validItems = items.filter(
            item => item.procedureCode && item.procedureName
        )
        if (validItems.length === 0) {
            toast.error('Preencha os dados dos procedimentos')
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        setLoading(true)
        try {
            if (isEditing && id) {
                const updateData: UpdateAuthorizationData = {
                    urgency: formData.urgency,
                    clinicalIndication: formData.clinicalIndication || undefined,
                    notes: formData.notes || undefined,
                }

                const response = await updateAuthorization(
                    parseInt(id, 10),
                    updateData
                )
                if (response.success) {
                    toast.success('Autorização atualizada com sucesso!')
                    navigate(`/billing/authorizations/${id}`)
                } else {
                    toast.error(response.error || 'Erro ao atualizar autorização')
                }
            } else {
                const createData: CreateAuthorizationData = {
                    healthPlanId: parseInt(formData.healthPlanId, 10),
                    clientId: parseInt(formData.clientId, 10),
                    professionalId: formData.professionalId
                        ? parseInt(formData.professionalId, 10)
                        : undefined,
                    urgency: formData.urgency,
                    clinicalIndication: formData.clinicalIndication || undefined,
                    notes: formData.notes || undefined,
                    items: items
                        .filter(item => item.procedureCode && item.procedureName)
                        .map(item => ({
                            procedureCode: item.procedureCode,
                            procedureName: item.procedureName,
                            requestedQuantity: item.quantity,
                            unitValue: 0,
                            notes: item.justification,
                        })),
                }

                const response = await createAuthorization(createData)
                if (response.success && response.result) {
                    toast.success('Autorização criada com sucesso!')
                    navigate(`/billing/authorizations/${response.result.id}`)
                } else {
                    toast.error(response.error || 'Erro ao criar autorização')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar autorização')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados da autorização...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/billing/authorizations')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <MdVerified className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing
                                ? 'Editar Autorização'
                                : 'Nova Autorização'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize as informações da autorização'
                                : 'Solicite uma nova autorização'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Basic Info */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações Básicas
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Convenio{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.healthPlanId}
                                    onChange={e =>
                                        updateFormField(
                                            'healthPlanId',
                                            e.target.value
                                        )
                                    }
                                    required
                                    disabled={isEditing}
                                >
                                    <option value="">Selecione...</option>
                                    {healthPlans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Urgência
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.urgency}
                                    onChange={e =>
                                        updateFormField(
                                            'urgency',
                                            e.target.value as AuthorizationUrgency
                                        )
                                    }
                                >
                                    {(
                                        Object.keys(
                                            AUTHORIZATION_URGENCY_LABELS
                                        ) as AuthorizationUrgency[]
                                    ).map(urgency => (
                                        <option key={urgency} value={urgency}>
                                            {AUTHORIZATION_URGENCY_LABELS[urgency]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Paciente{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <ClientSelector
                                    value={formData.clientId ? Number(formData.clientId) : null}
                                    onChange={(id) => updateFormField('clientId', id ? String(id) : '')}
                                    label=""
                                    placeholder="Selecione..."
                                    disabled={isEditing}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Carteirinha{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Número da carteirinha"
                                    value={formData.cardNumber}
                                    onChange={e =>
                                        updateFormField(
                                            'cardNumber',
                                            e.target.value
                                        )
                                    }
                                    required
                                    disabled={isEditing}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Profissional Solicitante
                                        </span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={formData.professionalId ? Number(formData.professionalId) : null}
                                        onChange={(id) => updateFormField('professionalId', id ? String(id) : '')}
                                        label=""
                                        placeholder="Selecione..."
                                        disabled={isEditing}
                                    />
                                </div>
                            )}

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Data da Solicitação
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.requestDate}
                                    onChange={e =>
                                        updateFormField(
                                            'requestDate',
                                            e.target.value
                                        )
                                    }
                                    disabled={isEditing}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Clinical Indication */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Indicação Clínica
                        </h2>

                        <div className="form-control w-full">
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Descreva a indicação clínica e justificativa para a solicitação..."
                                value={formData.clinicalIndication}
                                onChange={e =>
                                    updateFormField(
                                        'clinicalIndication',
                                        e.target.value
                                    )
                                }
                                rows={4}
                            />
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Observações
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Observações adicionais..."
                                value={formData.notes}
                                onChange={e =>
                                    updateFormField('notes', e.target.value)
                                }
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                {/* Orders */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="card-title text-lg">
                                Procedimentos Solicitados
                                <span className="text-error">*</span>
                            </h2>
                            {!isEditing && (
                                <button
                                    type="button"
                                    onClick={addItem}
                                    className="btn btn-secondary btn-sm gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    Adicionar
                                </button>
                            )}
                        </div>

                        {items.length === 0 ? (
                            <div className="text-center py-6 text-base-content/60">
                                <p>Nenhum procedimento adicionado</p>
                                {!isEditing && (
                                    <button
                                        type="button"
                                        onClick={addItem}
                                        className="btn btn-secondary btn-sm mt-4 gap-2"
                                    >
                                        <MdAdd className="text-lg" />
                                        Adicionar Procedimento
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {items.map((item, index) => (
                                    <div
                                        key={index}
                                        className="bg-base-200/50 rounded-lg p-4 border border-base-300/50"
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <span className="badge badge-secondary">
                                                Procedimento {index + 1}
                                            </span>
                                            {!isEditing && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removeItem(index)
                                                    }
                                                    className="btn btn-ghost btn-xs text-error"
                                                >
                                                    <MdDelete className="text-lg" />
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                            <div className="form-control">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Código TUSS{' '}
                                                        <span className="text-error">
                                                            *
                                                        </span>
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered input-sm"
                                                    value={item.procedureCode}
                                                    onChange={e =>
                                                        updateItem(
                                                            index,
                                                            'procedureCode',
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isEditing}
                                                />
                                            </div>
                                            <div className="form-control md:col-span-2">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Procedimento{' '}
                                                        <span className="text-error">
                                                            *
                                                        </span>
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered input-sm"
                                                    value={item.procedureName}
                                                    onChange={e =>
                                                        updateItem(
                                                            index,
                                                            'procedureName',
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isEditing}
                                                />
                                            </div>
                                            <div className="form-control">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Quantidade
                                                    </span>
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="input input-bordered input-sm"
                                                    value={item.quantity}
                                                    onChange={e =>
                                                        updateItem(
                                                            index,
                                                            'quantity',
                                                            parseInt(
                                                                e.target.value,
                                                                10
                                                            ) || 1
                                                        )
                                                    }
                                                    disabled={isEditing}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-control mt-3">
                                            <label className="label py-1">
                                                <span className="label-text text-xs">
                                                    Justificativa
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm"
                                                placeholder="Justificativa específica para este procedimento..."
                                                value={item.justification || ''}
                                                onChange={e =>
                                                    updateItem(
                                                        index,
                                                        'justification',
                                                        e.target.value
                                                    )
                                                }
                                                disabled={isEditing}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/billing/authorizations')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
